import React from "react";

/**
 * SummaryCards
 * ─────────────
 * Props:
 *   metrics         — merged metrics object
 *   currency        — e.g. "AED"
 *   thisPeriodLabel — e.g. "May 2025" or "Apr 28 – May 4"
 *   prevPeriodLabel — e.g. "April 2025"
 */

const pct = (curr, prev) => {
    if (prev == null || prev === 0) return null;
    return (((curr - prev) / prev) * 100).toFixed(1);
};

const formatVal = (val, type, currency) => {
    if (val == null) return "—";
    switch (type) {
        case "currency":
            return `${currency} ${Number(val).toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })}`;
        case "percent":
            return `${Number(val).toFixed(1)}%`;
        default:
            return Number(val).toLocaleString("en-US");
    }
};

const Card = ({ label, icon, value, type, currency, previous, thisPeriodLabel, prevPeriodLabel }) => {
    const change = pct(value, previous);
    const positive = change !== null && Number(change) >= 0;

    // e.g. "▲ 12.5%  (May vs April)"
    const bracket = thisPeriodLabel && prevPeriodLabel
        ? ` (${prevPeriodLabel} → ${thisPeriodLabel})`
        : "";

    return (
        <div className="sc-card">
            <div className="sc-card-top">
                <span className="sc-icon">{icon}</span>
                <span className="sc-label">{label}</span>
            </div>
            <div className="sc-value">{formatVal(value, type, currency)}</div>

            {change !== null ? (
                <div className={`sc-change ${positive ? "pos" : "neg"}`}>
                    {positive ? "▲" : "▼"} {Math.abs(change)}%
                    {bracket && <span className="sc-change-bracket">{bracket}</span>}
                </div>
            ) : (
                <div className="sc-change neutral">No comparison data</div>
            )}
        </div>
    );
};

const SummaryCards = ({ metrics, currency, thisPeriodLabel, prevPeriodLabel, isClinicService, isSportsService, crewPlural = "Crew" }) => {
    const cards = [
        {
            label: isClinicService ? "Total Appointments" : "Total Bookings",
            icon: "📋",
            value: metrics.totalBookings?.current,
            previous: metrics.totalBookings?.previous,
            type: "number",
        },
        {
            label: "Jobs Completed",
            icon: "✅",
            value: metrics.totalJobsCompleted?.current,
            previous: metrics.totalJobsCompleted?.previous,
            type: "number",
        },
        {
            label: "Total Revenue",
            icon: "💰",
            value: metrics.totalRevenue?.current,
            previous: metrics.totalRevenue?.previous,
            type: "currency",
        },
        {
            label: isClinicService ? "Revenue / Appointment" : "Revenue / Booking",
            icon: "🧾",
            value: metrics.revenuePerBooking?.current,
            previous: metrics.revenuePerBooking?.previous,
            type: "currency",
        },
        {
            label: `Active ${crewPlural}`,
            icon: "👷",
            value: metrics.activeCrew?.current,
            previous: metrics.activeCrew?.previous,
            type: "number",
        },
        {
            label: "Completion Rate",
            icon: "📈",
            value: metrics.completedVsScheduledPct?.current,
            previous: metrics.completedVsScheduledPct?.previous,
            type: "percent",
        },
        {
            label: "Cancellation Rate",
            icon: "❌",
            value: metrics.cancelledCount?.current,
            previous: metrics.cancelledCount?.previous,
            type: "number",
        },
        {
            label: isClinicService ? "Repeat Patients" : isSportsService ? "Repeat Players" : "Repeat Customers",
            icon: "🔁",
            value: metrics.repeatCustomersPct?.current,
            previous: metrics.repeatCustomersPct?.previous,
            type: "percent",
        },
    ];

    return (
        <div className="sc-section">
            <h2 className="sc-section-title">Overview</h2>
            <div className="sc-grid">
                {cards.map((c) => (
                    <Card
                        key={c.label}
                        {...c}
                        currency={currency}
                        thisPeriodLabel={thisPeriodLabel}
                        prevPeriodLabel={prevPeriodLabel}
                    />
                ))}
            </div>
        </div>
    );
};

export default SummaryCards;